import { Phone, Store, Utensils, Ticket, Briefcase, Warehouse, Dumbbell, Check, CalendarClock, ClipboardCheck, Percent, Zap, Moon, FileText, Banknote } from "lucide-react";
import { Link } from "react-router-dom";
import { PHONE, PHONE_HREF, BALBOA_URL } from "@/data/site";
import { Seo } from "@/components/Seo";
import { CommercialQuoteForm } from "@/components/CommercialQuoteForm";

const SERVE = [
  { icon: Store, title: "Retail stores & showrooms", text: "Steady comfort for customers and merchandise." },
  { icon: Utensils, title: "Restaurants", text: "Dining rooms stay cool through the lunch and dinner rush." },
  { icon: Ticket, title: "Entertainment venues", text: "Systems maintained for heavy crowds." },
  { icon: Briefcase, title: "Offices", text: "Comfortable, productive workspaces." },
  { icon: Warehouse, title: "Warehouses", text: "Rooftop and large-capacity systems." },
  { icon: Dumbbell, title: "Gyms & fitness centers", text: "High heat and humidity loads handled." },
];

const VISIT_CHECKLIST = ["Filter changes", "Coil inspection", "Condensate drain line service", "Belt inspection", "Refrigerant level check", "Electrical check", "Written service report for your records"];

const STEPS = [
  "Request a free site visit",
  "We assess your systems and build a custom plan and quote",
  "Scheduled maintenance begins, with a written report after every visit",
];

const scrollToForm = () => document.getElementById("commercial-quote")?.scrollIntoView({ behavior: "smooth" });

const CommercialHero = () => (
  <section className="bg-brand-ink text-white">
    <div className="max-w-7xl mx-auto px-4 py-20 sm:py-28">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-3">Commercial Maintenance Contracts</p>
      <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-black max-w-3xl">Commercial AC Maintenance, Built Around Your Business</h1>
      <p className="text-base sm:text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed">
        Custom maintenance plans for South Florida businesses, scheduled early morning or after hours so your customers never notice. Priority emergency response and a written report after every visit.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 mt-8">
        <button onClick={scrollToForm} data-testid="commercial-hero-cta"
          className="bg-brand-ice hover:bg-[#7FA8F0] text-brand-ink font-bold px-8 py-4 rounded-sm transition-colors duration-200">
          Request a Free Site Visit
        </button>
        <a href={PHONE_HREF} data-testid="commercial-hero-call"
          className="border-2 border-white text-white hover:bg-white hover:text-brand-ink font-bold px-8 py-4 rounded-sm text-center flex items-center justify-center gap-2 transition-colors duration-200">
          <Phone className="w-5 h-5" /> Call {PHONE}
        </a>
      </div>
    </div>
  </section>
);

const WhoWeServe = () => (
  <section className="bg-white">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
      <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-10">Who we serve</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVE.map((s) => (
          <div key={s.title} data-testid={`commercial-serve-${s.title.split(" ")[0].toLowerCase().replace("&", "")}`}
            className="border border-zinc-200 p-6 rounded-sm hover:border-brand transition-colors duration-200">
            <s.icon className="w-7 h-7 text-brand mb-3" />
            <h3 className="font-heading font-bold text-lg text-brand-ink">{s.title}</h3>
            <p className="text-sm text-zinc-600 mt-1">{s.text}</p>
          </div>
        ))}
      </div>
      <p data-testid="commercial-trusted-line" className="text-sm text-zinc-600 mt-8">
        Trusted by luxury retail showrooms, indoor entertainment venues and restaurants across South Florida.
      </p>
      <p className="text-sm text-zinc-600 mt-2">
        Manage a condo building? See our{" "}
        <Link to="/condo-associations" data-testid="commercial-condo-link" className="text-brand font-bold underline hover:text-brand-dark transition-colors duration-200">Association Partner Program</Link>.
      </p>
    </div>
  </section>
);

const ScheduleAndChecklist = () => (
  <section className="bg-brand-subtle">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10">
      <div>
        <CalendarClock className="w-8 h-8 text-brand mb-4" />
        <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink">Your schedule, your way</h2>
        <p className="text-zinc-600 mt-4 leading-relaxed">
          We set the visit schedule around your business: monthly, every two months, or quarterly, based on what your systems need.
        </p>
        <div className="mt-8">
          <h3 className="font-heading font-bold text-xl text-brand-ink flex items-center gap-2"><Banknote className="w-5 h-5 text-brand" /> Pricing</h3>
          <p data-testid="commercial-pricing-text" className="text-zinc-600 mt-2 leading-relaxed">
            Every business is different. Your free site visit lets us recommend the right schedule and give you an exact price for your plan.
          </p>
        </div>
      </div>
      <div className="bg-white border border-zinc-200 p-8 rounded-sm">
        <ClipboardCheck className="w-8 h-8 text-brand mb-4" />
        <h2 className="font-heading font-black text-2xl text-brand-ink">Every visit includes</h2>
        <ul className="mt-5 space-y-3">
          {VISIT_CHECKLIST.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-zinc-700">
              <Check className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const BENEFITS = [
  { icon: Percent, text: "15% off all parts and repairs" },
  { icon: Zap, text: "Priority emergency response" },
  { icon: Moon, text: "Early-morning or after-hours service so work never disrupts your customers" },
  { icon: FileText, text: "Individual invoicing per location, or one combined invoice for multiple locations: your choice" },
];

const MemberBenefits = () => (
  <section className="bg-brand-ink text-white">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
      <h2 className="text-2xl sm:text-3xl tracking-tight font-black">Plan member benefits</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10">
        {BENEFITS.map((b) => (
          <div key={b.text} className="flex items-start gap-3 bg-white/5 border border-white/10 p-5 rounded-sm">
            <b.icon className="w-5 h-5 text-brand-ice shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-200">{b.text}</p>
          </div>
        ))}
        <div className="flex items-start gap-3 bg-white/5 border border-white/10 p-5 rounded-sm sm:col-span-2">
          <Banknote className="w-5 h-5 text-brand-ice shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-200">
            Commercial financing up to $500,000 through{" "}
            <a href={BALBOA_URL} target="_blank" rel="noopener noreferrer" data-testid="commercial-balboa-link" className="text-brand-ice underline hover:text-white transition-colors duration-200">Balboa Capital</a>
          </p>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="bg-white">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
      <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-10">How it works</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {STEPS.map((step, i) => (
          <div key={step} data-testid={`commercial-step-${i + 1}`} className="border-l-4 border-brand bg-brand-subtle p-6 rounded-sm">
            <span className="font-heading font-black text-3xl text-brand">{i + 1}</span>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{step}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Commercial() {
  return (
    <div data-testid="commercial-page">
      <Seo title="Commercial AC Maintenance Plans in South Florida | Central Air Depot" description="Custom AC maintenance plans for retail, restaurants, entertainment venues, offices, warehouses and gyms. Free site visit, priority emergency response." />
      <CommercialHero />
      <WhoWeServe />
      <ScheduleAndChecklist />
      <MemberBenefits />
      <HowItWorks />
      <section id="commercial-quote" className="bg-brand-subtle">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-2">Request a Free Site Visit</h2>
          <p className="text-zinc-600 mb-8">Tell us about your business and a commercial specialist will schedule your free site visit.</p>
          <CommercialQuoteForm />
        </div>
      </section>
    </div>
  );
}
